import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import type { ArticleTeaser } from '@/types';
import { Tag } from '@components/ui';

interface ArticleTeaserCardProps {
  article: ArticleTeaser;
  accent: string | undefined;
  /** Libelle du lien de lecture, deja traduit. */
  more: string;
  /** aria-label de la liste de themes. */
  themes: string;
  /** Numero affiche dans l'index ; absent sur la section d'accueil. */
  num?: string;
}

/**
 * Carte d'article, commune a la section d'accueil et a l'index.
 *
 * Le rendu depend de `isExternal` : en francais l'article est heberge ici, donc
 * navigation interne par `Link` ; en anglais il vit sur le blog Asterion, donc
 * ancre externe ouverte dans un onglet, avec `rel="noopener noreferrer"`.
 */
export default function ArticleTeaserCard(
  { article, accent, more, themes, num }: ArticleTeaserCardProps,
): JSX.Element {
  const style = { '--article-color': accent } as React.CSSProperties;

  const inner = (
    <>
      <div className="ArticleCard__accent" aria-hidden="true" />
      {num !== undefined && <span className="ArticleCard__num">{num}</span>}
      <h3 className="ArticleCard__name">{article.title}</h3>
      <p className="ArticleCard__desc">{article.excerpt}</p>
      <div className="ArticleCard__tags" role="list" aria-label={themes}>
        <Tag>{article.category}</Tag>
      </div>
      <span className="ArticleCard__more">{more}</span>
    </>
  );

  if (article.isExternal) {
    return (
      <a
        className="ArticleCard"
        href={article.href}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link className="ArticleCard" to={article.href} style={style}>
      {inner}
    </Link>
  );
}
